import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import CustomerList from "../components/CustomerList";
import UserList from "../components/UserList";
import ClientiiMeiList from "../components/ClientiiMeiList";
import OrdersList from "../components/OrdersList";
import useAdminData from "../hooks/useAdminData";
import useOrdersData from "../hooks/useOrdersData";
import StocPage from "../components/StocPage";
import AllOrdersList from "../components/AllOrdersList";
import { useTheme } from "../context/ThemeContext";
import API_BASE_URL from "../config";
import { getUserRole } from "../utils/Auth";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import DatePicker from "react-datepicker";
import { Calendar } from "lucide-react";
import "react-datepicker/dist/react-datepicker.css";
import { CheckCircle, XCircle } from "lucide-react";

const AdminDashboard = () => {
  const { darkMode } = useTheme();
  const location = useLocation();
  const [view, setView] = useState("dashboard");
  const today = new Date();
  const formattedDate = today.toLocaleDateString("ro-RO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const romanianMonthNames = [
    "Ianuarie",
    "Februarie",
    "Martie",
    "Aprilie",
    "Mai",
    "Iunie",
    "Iulie",
    "August",
    "Septembrie",
    "Octombrie",
    "Noiembrie",
    "Decembrie",
  ];

  const currentDate = new Date();
  const currentMonthName = romanianMonthNames[currentDate.getMonth()];
  const currentYear = currentDate.getFullYear();
  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  useEffect(() => {
    if (location.pathname === "/dashboard") {
      setView("dashboard");
    }
  }, [location.pathname]);

  const {
    userName,
    hasCommission,
    loading,
    error,
    customers,
    myCustomers,
    users,
    fetchCustomers,
    fetchMyCustomers,
    fetchUsers,
  } = useAdminData();

  const { orders, fetchOrders, fetchAllOrders } = useOrdersData();
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [recalculateResults, setRecalculateResults] = useState([]);
  const [isConfirmRecalculateOpen, setIsConfirmRecalculateOpen] =
    useState(false);

  useEffect(() => {
    if (view === "orders" && myCustomers.length === 0) {
      fetchMyCustomers();
    }
    if (view === "allOrders" && !ordersLoaded) {
      fetchAllOrders();
      setOrdersLoaded(true);
    }
  }, [
    view,
    myCustomers.length,
    fetchMyCustomers,
    fetchAllOrders,
    ordersLoaded,
  ]);

  // 🔥 Date statistice API
  const [userStats, setUserStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [errorStats, setErrorStats] = useState(null);
  const role = getUserRole();
  const [showStats, setShowStats] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [monthlySales, setMonthlySales] = useState(0);
  const [commission, setCommission] = useState(0);
  const [loadingCommission, setLoadingCommission] = useState(true);
  const [errorCommission, setErrorCommission] = useState(null);
  const [dailySales, setDailySales] = useState([]);
  const [loadingDailySales, setLoadingDailySales] = useState(true);
  const [errorDailySales, setErrorDailySales] = useState(null);
  const [isRecalculateModalOpen, setIsRecalculateModalOpen] = useState(false);
  const [recalculateLoading, setRecalculateLoading] = useState(false);
  const [commissionApprovals, setCommissionApprovals] = useState([]);
  const [loadingApprovals, setLoadingApprovals] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7) // YYYY-MM
  );

  useEffect(() => {
    const fetchDailySales = async () => {
      try {
        const token = localStorage.getItem("token");

        const endpoint =
          role === "Admin"
            ? `${API_BASE_URL}/api/users/today-sales`
            : `${API_BASE_URL}/api/users/my-today-sales`;

        const response = await fetch(endpoint, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Eroare la încărcarea vânzărilor zilnice.");
        }

        const data = await response.json();

        // Ha admin, az lista, ha agent, az egy objektum, amit listává kell konvertálni
        setDailySales(role === "Admin" ? data : [data]);
      } catch (error) {
        setErrorDailySales(error.message);
      } finally {
        setLoadingDailySales(false);
      }
    };

    fetchDailySales();
  }, [role]);

  const fetchCommissionApprovals = async () => {
    if (role !== "Admin" || !showStats) return;

    setLoadingApprovals(true);
    try {
      const token = localStorage.getItem("token");
      const [year, month] = selectedMonth.split("-");

      const res = await fetch(
        `${API_BASE_URL}/api/admin/commissions?year=${year}&month=${month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!res.ok) throw new Error("Eroare la comisioane");

      setCommissionApprovals(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingApprovals(false);
    }
  };

  const approveCommission = async (userId) => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/admin/commissions/approve`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId,
            monthYear: selectedMonth,
          }),
        }
      );

      if (!res.ok) throw new Error("Eroare la aprobare");

      // 🔥 frissítjük a listát
      fetchCommissionApprovals();
    } catch (err) {
      alert(err.message);
    }
  };

  useEffect(() => {
    if (role === "Admin" && showStats && view === "dashboard") {
      fetchCommissionApprovals();
    }
  }, [role, showStats, view, selectedMonth]);

  useEffect(() => {
    if (role !== "Agent") return;

    const fetchCommission = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_BASE_URL}/api/users/me/commission`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Hiba a jutalék adatainak betöltésekor.");
        }

        const data = await response.json();

        setMonthlySales(data.monthlySalesTotal ?? 0);
        setCommission(data.commissionEarned ?? 0);

        // 🔥 Mentjük az aktuális hónapot a localStorage-ban
        const currentMonth = new Date().getMonth();
        localStorage.setItem("lastFetchedMonth", currentMonth.toString());
      } catch (err) {
        setErrorCommission(err.message);
      } finally {
        setLoadingCommission(false);
      }
    };

    fetchCommission();

    // 🔥 FIGYELJÜK A HÓNAPVÁLTÁST
    const interval = setInterval(() => {
      const currentMonth = new Date().getMonth();
      const storedMonth = localStorage.getItem("lastFetchedMonth");

      if (storedMonth !== currentMonth.toString()) {
        console.log("Új hónap van, frissítjük az adatokat...");
        localStorage.setItem("lastFetchedMonth", currentMonth.toString());
        fetchCommission();
      }
    }, 1000 * 60 * 60); // 🔥 1 óránként ellenőrizzük

    return () => clearInterval(interval);
  }, [role]); // ✅ Helyesen zárt useEffect

  useEffect(() => {
    const fetchUserStatistics = async () => {
      try {
        if (role !== "Admin" || !showStats) return;

        const token = localStorage.getItem("token");
        const response = await fetch(`${API_BASE_URL}/api/users/statistics`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Eroare la încărcarea statisticilor.");
        }

        const data = await response.json();
        setUserStats(data);
      } catch (err) {
        setErrorStats(err.message);
      } finally {
        setLoadingStats(false);
      }
    };

    if (role === "Admin" && showStats) {
      fetchUserStatistics();
    }
  }, [role, showStats]);

  const checkPassword = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        throw new Error("Parolă incorectă!");
      }

      setShowStats(true);
      setIsPasswordModalOpen(false);
      setPasswordError(null);
    } catch (error) {
      setPasswordError(error.message);
    }
  };

  return (
    <div className={`flex ${darkMode ? "bg-gray-900" : "bg-gray-200"}`}>
      <Sidebar
        setView={setView}
        view={view}
        fetchCustomers={fetchCustomers}
        fetchMyCustomers={fetchMyCustomers}
        fetchUsers={fetchUsers}
        fetchOrders={fetchOrders}
        fetchAllOrders={fetchAllOrders}
      />
      <div
        className={`flex-1 p-2 ml-4 ${darkMode ? "text-white" : "text-black"}`}
      >
        {error && <p className="text-red-500">Eroare: {error}</p>}

        {view === "dashboard" && (
          <div>
            <h2
              className={`text-3xl font-semibold ${darkMode ? "text-white" : "text-black"
                } mb-4`}
            >
              Bun venit, {userName}!
            </h2>
            <p
              className={`text-xl ${darkMode ? "text-white" : "text-black"
                } mb-4`}
            >
              Aici poți gestiona utilizatorii și clienții.
            </p>

            {role === "Agent" && (
              <div
                className={`mt-6 p-4 shadow-md rounded-lg ${darkMode ? "bg-gray-800 text-white" : "bg-white text-black"
                  }`}
              >
                <h3 className="text-xl font-semibold mb-2">
                  📊 Statistici personale
                </h3>
                {loadingCommission ? (
                  <p>Se încarcă...</p>
                ) : errorCommission ? (
                  <p className="text-red-500">{errorCommission}</p>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      className={`p-4 rounded-lg ${darkMode ? "bg-gray-700" : "bg-gray-200"
                        }`}
                    >
                      <p className="text-lg font-medium">📈 Vânzări lunare</p>
                      <p className="text-2xl font-bold">
                        {typeof monthlySales === "number"
                          ? monthlySales.toFixed(2)
                          : "N/A"}{" "}
                        €
                      </p>
                    </div>
                    {hasCommission && (
                      <div
                        className={`p-4 rounded-lg ${darkMode ? "bg-gray-700" : "bg-gray-200"
                          }`}
                      >
                        <p className="text-lg font-medium">💰 Comision</p>
                        <p className="text-2xl font-bold">
                          {typeof commission === "number"
                            ? commission.toFixed(2)
                            : "N/A"}{" "}
                          €
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 🔥 Admin statisztikai megjelenítés JELSZÓVAL VÉDETTEN */}
            {role === "Admin" && (
              <>
                <h3 className="text-xl font-semibold mb-4">
                  📊 Statistici utilizatori – {currentMonthName} {currentYear}
                </h3>

                {!showStats ? (
                  <button
                    onClick={() => setIsPasswordModalOpen(true)}
                    className="px-4 py-4 bg-blue-600 text-white rounded-3xl hover:bg-blue-800 transition"
                  >
                    🔐 Introduceți parola pentru a vedea statisticile lunare
                  </button>
                ) : loadingStats ? (
                  <p>Se încarcă...</p>
                ) : errorStats ? (
                  <p className="text-red-500">{errorStats}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table
                      className={`w-full border ${darkMode
                        ? "border-gray-700 bg-gray-900 text-gray-300"
                        : "border-gray-400 bg-white text-gray-800 shadow-lg"
                        }`}
                    >
                      <thead>
                        <tr
                          className={`${darkMode
                            ? "bg-gray-700 text-white"
                            : "bg-gray-300 text-gray-900"
                            }`}
                        >
                          <th className="border p-2">Nume</th>
                          <th className="border p-2">Email</th>
                          <th className="border p-2">Rol</th>
                          <th className="border p-2">
                            Eligibil pentru comision?
                          </th>
                          <th className="border p-2">Vânzări lunare (€)</th>
                          <th className="border p-2">Comision (€)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userStats.length > 0 ? (
                          userStats.map((user, index) => (
                            <tr
                              key={user.id}
                              className={`border ${darkMode
                                ? index % 2 === 0
                                  ? "bg-gray-800 hover:bg-gray-700"
                                  : "bg-gray-900 hover:bg-gray-700"
                                : index % 2 === 0
                                  ? "bg-gray-50 hover:bg-gray-200"
                                  : "bg-white hover:bg-gray-200"
                                } transition text-center`}
                            >
                              <td className="border p-2">{user.name}</td>
                              <td className="border p-2">{user.email}</td>
                              <td className="border p-2">{user.role}</td>
                              <td className="border p-2">
                                {user.hasCommission ? "✅" : "❌"}
                              </td>
                              <td className="border p-2">
                                {user.monthlySalesTotal.toFixed(2)} €
                              </td>
                              <td className="border p-2">
                                {user.commissionEarned.toFixed(2)} €
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="6"
                              className="text-center p-4 text-gray-400"
                            >
                              Nu există date statistice disponibile.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
                <button
                  onClick={() => setIsConfirmRecalculateOpen(true)}
                  className="px-4 py-4 bg-green-600 text-white rounded-3xl hover:bg-green-800 transition mt-4 mx-10"
                >
                  🔄 Recalculare și vizualizare modificări
                </button>

                {showStats && (
                  <div className={`mt-10 p-6 rounded-2xl shadow-lg ${darkMode ? "bg-gray-800 text-white" : "bg-white text-black"
                    }`}>
                    <div className="flex items-center gap-4 mb-4">
                      <h3 className="text-2xl font-semibold">
                        💰 Aprobare comisioane
                      </h3>

                      <DatePicker
                        selected={
                          selectedMonth
                            ? new Date(selectedMonth + "-01T00:00:00")
                            : null
                        }
                        onChange={(d) => {
                          if (!d) return;
                          const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                          setSelectedMonth(m);
                        }}
                        dateFormat="MM/yyyy"
                        showMonthYearPicker
                        calendarStartDay={1}
                        portalId="datepicker-root"
                        customInput={
                          <div
                            className={`flex items-center justify-between w-[200px] h-[44px] px-4 
          rounded-xl shadow-sm border-2 transition-all cursor-pointer
          ${darkMode
                                ? "bg-gray-800 text-gray-100 border-gray-600 hover:bg-gray-700"
                                : "bg-white text-gray-800 border-gray-300 hover:bg-gray-100"
                              }`}
                          >
                            <span className="font-semibold text-sm">
                              {selectedMonth
                                ? capitalize(
                                  new Date(selectedMonth + "-01T00:00:00").toLocaleDateString(
                                    "ro-RO",
                                    { year: "numeric", month: "long" }
                                  )
                                )
                                : "Alege luna"}
                            </span>

                            <Calendar
                              className={`w-5 h-5 ${darkMode ? "text-gray-200" : "text-gray-700"
                                }`}
                            />
                          </div>
                        }
                      />
                    </div>


                    {loadingApprovals ? (
                      <p>Se încarcă...</p>
                    ) : commissionApprovals.length === 0 ? (
                      <p className="opacity-70">
                        Nu există comisioane pentru această lună.
                      </p>
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="py-2 text-left">Utilizator</th>
                            <th className="py-2 text-right">Vânzări</th>
                            <th className="py-2 text-right">Comision</th>
                            <th className="py-2 text-center">Status</th>
                            <th className="py-2 text-center w-[140px]">Acțiuni</th>
                          </tr>
                        </thead>

                        <tbody>
                          {commissionApprovals.map((c) => (
                            <tr key={c.userId} className="border-b">
                              <td className="py-2">{c.userName}</td>

                              <td className="py-2 text-right">
                                {c.salesTotal.toFixed(2)} €
                              </td>

                              <td className="py-2 text-right font-semibold">
                                {c.commissionTotal.toFixed(2)} €
                              </td>

                              <td className="py-2 text-center">
                                <span
                                  className={`inline-flex items-center justify-center gap-1
    w-[110px] h-[28px]
    rounded-full text-xs font-semibold border
    ${c.status === "PLATIT"
                                      ? darkMode
                                        ? "bg-gray-700/40 text-green-300 border-gray-600"
                                        : "bg-green-100 text-green-700 border-green-300"
                                      : darkMode
                                        ? "bg-gray-700/40 text-red-300 border-gray-600"
                                        : "bg-red-100 text-red-700 border-red-300"
                                    }`}
                                >
                                  {c.status === "PLATIT" ? (
                                    <>
                                      <CheckCircle size={14} className="opacity-80" />
                                      Plătit
                                    </>
                                  ) : (
                                    <>
                                      <XCircle size={14} className="opacity-80" />
                                      Neplătit
                                    </>
                                  )}
                                </span>

                              </td>
<td className="py-2 text-center w-[140px]">
                                {c.status === "PLATIT" ? (
                                  <span className="text-green-500 font-semibold">—</span>
                                ) : (
                                  <button
                                    onClick={() => approveCommission(c.userId)}
                                    className="px-3 py-1 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition"
                                  >
                                    ✔ Aprobă
                                  </button>
                                )}
                              </td>
                            </tr>

                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

              </>
            )}
          </div>
        )}

        <div className="mt-8">
          <h3 className="text-xl font-semibold mb-2">
            📅 Vânzări - {formattedDate}
          </h3>

          {loadingDailySales ? (
            <p>Se încarcă...</p>
          ) : errorDailySales ? (
            <p className="text-red-500">{errorDailySales}</p>
          ) : dailySales.length === 0 ? (
            <p>Nu există vânzări pentru azi.</p>
          ) : (
            <>
              {role === "Admin" && (
                <div className={`flex gap-6 mb-3`}>
                  <div className={`px-5 py-3 rounded-xl font-semibold text-sm shadow ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-800 border border-gray-300"}`}>
                    📦 Total comenzi:{" "}
                    <span className="text-lg font-bold">
                      {dailySales.reduce((sum, item) => sum + item.totalOrders, 0)}
                    </span>
                  </div>
                  <div className={`px-5 py-3 rounded-xl font-semibold text-sm shadow ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-800 border border-gray-300"}`}>
                    💶 Total vânzări:{" "}
                    <span className="text-lg font-bold">
                      {dailySales.reduce((sum, item) => sum + item.totalSales, 0).toFixed(2)} €
                    </span>
                  </div>
                </div>
              )}
              <table
                className={`w-full border mt-2 ${darkMode
                  ? "border-gray-700 bg-gray-900 text-gray-300"
                  : "border-gray-400 bg-white text-gray-800 shadow-md"
                  }`}
              >
                <thead>
                  <tr
                    className={`bg-gray-300 text-black dark:bg-gray-700 dark:text-white`}
                  >
                    {role === "Admin" && <th className="p-2 border">Agent</th>}
                    <th className="p-2 border">Comenzi</th>
                    <th className="p-2 border">Total Vânzări (€)</th>
                  </tr>
                </thead>
                <tbody>
                  {dailySales.map((item, index) => (
                    <tr
                      key={index}
                      className={`text-center ${darkMode
                        ? index % 2 === 0
                          ? "bg-gray-800"
                          : "bg-gray-900"
                        : index % 2 === 0
                          ? "bg-gray-50"
                          : "bg-white"
                        }`}
                    >
                      {role === "Admin" && (
                        <td className="p-2 border">{item.userName}</td>
                      )}
                      <td className="p-2 border">{item.totalOrders}</td>
                      <td className="p-2 border">
                        {item.totalSales.toFixed(2)} €
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        {view === "customers" && (
          <CustomerList customers={customers} fetchCustomers={fetchCustomers} />
        )}
        {view === "myCustomers" && (
          <ClientiiMeiList
            myCustomers={myCustomers}
            fetchMyCustomers={fetchMyCustomers}
          />
        )}
        {view === "users" && <UserList users={users} fetchUsers={fetchUsers} />}
        {view === "orders" && (
          <OrdersList
            orders={orders}
            fetchOrders={fetchOrders}
            customers={myCustomers}
          />
        )}
        {view === "allOrders" && (
          <AllOrdersList users={users} fetchAllOrders={fetchAllOrders} />
        )}
        {view === "stoc" && <StocPage />}

        <Dialog
          open={isPasswordModalOpen}
          onClose={() => setIsPasswordModalOpen(false)}
          maxWidth="sm" // 🔥 Maximális szélesség
          PaperProps={{
            sx: { borderRadius: "16px", width: 400 }, // 🔥 Kerekített szélek és nagyobb szélesség
          }}
        >
          <DialogTitle sx={{ fontWeight: "bold", textAlign: "center" }}>
            Introduceți parola pentru a accesa statisticile
          </DialogTitle>
          <DialogContent sx={{ padding: 3 }}>
            <TextField
              type="password"
              label="Parola"
              variant="outlined"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!passwordError}
              helperText={passwordError || ""}
              sx={{ mt: 2, borderRadius: "8px" }}
            />
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", paddingBottom: 2 }}>
            <Button
              onClick={() => setIsPasswordModalOpen(false)}
              color="secondary"
              sx={{ borderRadius: "20px", paddingX: 3 }}
            >
              Anulare
            </Button>
            <Button
              onClick={checkPassword}
              variant="contained"
              color="primary"
              sx={{ borderRadius: "20px", paddingX: 3 }}
            >
              Verificare
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={isRecalculateModalOpen}
          onClose={() => setIsRecalculateModalOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              p: 2,
            },
          }}
        >
          <DialogTitle>📊 Rezultate recalculare vânzări</DialogTitle>
          <DialogContent>
            {recalculateLoading ? (
              <p>Se calculează...</p>
            ) : recalculateResults.length === 0 ? (
              <p>Nu există vânzări.</p>
            ) : (
              <table
                className={`w-full border mt-2 ${darkMode
                  ? "border-gray-700 bg-gray-900 text-gray-300"
                  : "border-gray-400 bg-white text-gray-800 shadow-md"
                  }`}
              >
                <thead>
                  <tr
                    className={`${darkMode
                      ? "bg-gray-700 text-white"
                      : "bg-gray-300 text-black"
                      }`}
                  >
                    <th className="p-2 border">Agent</th>
                    <th className="p-2 border">Vânzări vechi (€)</th>
                    <th className="p-2 border">Vânzări noi (€)</th>
                  </tr>
                </thead>
                <tbody>
                  {recalculateResults.map((item, index) => (
                    <tr
                      key={item.userId}
                      className={`${darkMode
                        ? index % 2 === 0
                          ? "bg-gray-800"
                          : "bg-gray-900"
                        : index % 2 === 0
                          ? "bg-gray-50"
                          : "bg-white"
                        } text-center`}
                    >
                      <td className="p-2 border">{item.userName}</td>
                      <td className="p-2 border">
                        {item.oldSalesTotal.toFixed(2)} €
                      </td>
                      <td className="p-2 border">
                        {item.newSalesTotal.toFixed(2)} €
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setIsRecalculateModalOpen(false)}
              color="primary"
            >
              Închide
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={isConfirmRecalculateOpen}
          onClose={() => setIsConfirmRecalculateOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              p: 2,
            },
          }}
        >
          <DialogTitle sx={{ color: "red", fontWeight: "bold" }}>
            Atenție - Confirmare recalculare
          </DialogTitle>
          <DialogContent>
            <p>
              Sigur doriți să recalculați vânzările? Această acțiune nu poate fi
              anulată.
            </p>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3 }}>
            <Button
              onClick={() => setIsConfirmRecalculateOpen(false)}
              color="secondary"
              sx={{ borderRadius: "20px", paddingX: 3 }}
            >
              Nu, renunță
            </Button>
            <Button
              onClick={async () => {
                setIsConfirmRecalculateOpen(false);
                setIsRecalculateModalOpen(true);
                setRecalculateLoading(true);

                try {
                  const token = localStorage.getItem("token");
                  const response = await fetch(
                    `${API_BASE_URL}/api/users/recalculate-user-sales`,
                    {
                      method: "POST",
                      headers: { Authorization: `Bearer ${token}` },
                    }
                  );

                  if (!response.ok) throw new Error("Eroare la recalculare.");

                  const data = await response.json();
                  setRecalculateResults(data);
                } catch (err) {
                  alert(err.message);
                } finally {
                  setRecalculateLoading(false);
                }
              }}
              variant="contained"
              color="error"
              sx={{ borderRadius: "20px", paddingX: 3 }}
            >
              Da, recalculează
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
